import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logger } from "whole-shared";

import { port, revision } from "../env";
import { ws } from "../ws";
import { RequestButton } from "./Components";
import { Context } from "./context";

/** Height of the fixed site header; keep in sync with route offset in `Whole`. */
export const SITE_HEADER_HEIGHT_PX = 72;

const Avatar = ({ url }: { url: string }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    const key = `google-avatar-${url}`;
    const cached = localStorage.getItem(key);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    if(cached) setSrc(cached);
    else {
      fetch(url)
        .then(res => {
          if(! res.ok) throw new Error(`Avatar fetch failed: ${res.status} ${res.statusText}`);

          return res.blob();
        })
        .then(blob => {
          const reader = new FileReader();

          reader.onloadend = () => {
            if(typeof reader.result !== "string") return logger.error("Unexpected type of reader.result", reader.result);

            localStorage.setItem(key, reader.result);
            setSrc(reader.result);
          };

          reader.readAsDataURL(blob);
        })
        .catch(error => logger.error("While fetching avatar", error));
    }
  }, [url]);

  return src ? <img src={src} style={{ borderRadius: "50%", objectFit: "cover", paddingRight: 10, width: 30 }} /> : null;
};

const Queue = () => {
  const { player, updateContext } = useContext(Context);
  const [connected, setConnected] = useState(ws.isConnected());
  const [inQueue, setInQueue] = useState(false);
  const [matchId, setMatchId] = useState("");
  const navigate = useNavigate();
  const exitQueue = useCallback(() => setInQueue(false), []);
  const request = useMemo(() => ({ queue: { exitQueue: true } }), []);

  useEffect(() => {
    const detachConnection = ws.on("connection", connected => {
      setInQueue(false);
      setConnected(connected);
    });

    const detachInQueue = ws.on("inQueue", setInQueue);
    const detachMatch = ws.on("match", setMatchId);

    return () => {
      detachConnection();
      detachInQueue();
      detachMatch();
    };
  }, []);

  return <div style={{ alignItems: "center", display: "flex", marginLeft: "auto", marginRight: 15, position: "relative", width: 230 }}>
    {connected ? (
      player.isLogged() ? <div style={{ alignItems: "center", display: "flex" }}>
        {matchId ? <>
          {"Match found:"}
          <button onClick={() => navigate(`/match/${matchId}`, {})} style={{ marginLeft: 15 }}>
            {"Fight!"}
          </button>
        </> : inQueue ? <>
          {"Finding match..."}
          <img src="/loading.gif" width="17" style={{ marginLeft: 10, marginRight: 10 }} />
          <RequestButton onResponse={exitQueue} request={request} text="Cancel" />
        </> : <button onClick={() => updateContext({ queueSettings: true })}>Find match</button>}
      </div> : null
    ) : <>
      {"Connecting..."}
      <img src="/loading.gif" width="40" style={{ marginLeft: 15 }} />
    </>}
  </div>;
};

export const Header = () => {
  const { connected, inMatch, player, stage } = useContext(Context);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const redirect = useCallback(
    (action: "login" | "logout") => () => (window.location.href = `${stage === "dev" ? `http://localhost:${port}` : ""}/auth/google/${action}`),
    [stage]
  );

  const handleOpenMenu = useCallback(() => {
    if(menuOpen) return;

    const button = document.getElementById("profile-button")!;
    const menu = document.getElementById("profile-menu")!;

    setMenuOpen(true);
    button.classList.add("high-lighted");
    menu.style.maxHeight = "200px";

    const handleCloseMenu = () => {
      setMenuOpen(false);
      button.classList.remove("high-lighted");
      menu.style.maxHeight = "0px";
      document.removeEventListener("click", handleCloseMenu);
    };

    setTimeout(() => document.addEventListener("click", handleCloseMenu));
  }, [menuOpen]);

  return inMatch && connected ? null : <header
    style={{
      alignItems:      "center",
      backgroundColor: "#242424",
      boxSizing:       "border-box",
      display:         "flex",
      left:            0,
      minHeight:       SITE_HEADER_HEIGHT_PX,
      padding:         10,
      position:        "fixed",
      right:           0,
      top:             0,
      zIndex:          4
    }}
  >
    <nav style={{ alignItems: "center", display: "flex" }}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {[
          ["Home", "/"],
          ["test", "/test"]
        ].map(([desc, url]) => <li key={url} style={{ display: "inline-block", marginRight: 20 }}>
          <Link to={url}>{desc}</Link>
        </li>)}
        {player.isAdmin() ? <li style={{ display: "inline-block", marginRight: 20 }}>revision: {revision}</li> : null}
      </ul>
    </nav>
    <Queue />
    <div style={{ position: "relative" }}>
      {player.isLogged() ? <>
        <button className="profile-button" id="profile-button" onClick={handleOpenMenu}>
          {player.photo ? <Avatar url={player.photo} /> : null}
          {player.givenName}
        </button>
        <div className="profile-menu" id="profile-menu">
          <div className="panel">
            <div className="profile-menu-item" onClick={() => navigate("/settings")}>
              {"Settings"}
            </div>
            <hr />
            <div
              className="profile-menu-item"
              onClick={() => {
                ws.request({ queue: { exitQueue: true } });
                redirect("logout")();
              }}
            >
              {"Log out"}
            </div>
          </div>
        </div>
      </> : <button className="profile-button" onClick={redirect("login")}>
        {"Log in"}
      </button>}
    </div>
  </header>;
};
